import { React, useState, useEffect, useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Moment from "moment";
import "moment/locale/ko";
import Swal from "sweetalert2";
import { AppContext } from '../../../../AppStore';

const layoutStyles = {
  page: { display: "flex", flexDirection: "column", gap: "12px", padding: "0 10px 24px", height: "100%", boxSizing: "border-box" },
  container: { display: "flex", gap: "16px", flexWrap: "wrap", height: "100%", minHeight: 0, alignItems: "stretch", boxSizing: "border-box" },
  sidebar: { flex: "0 0 320px", background: "#fff", borderRadius: "16px", padding: "14px", boxShadow: "0 4px 16px rgba(0,0,0,0.08)", minHeight: "460px", display: "flex", flexDirection: "column", height: "100%" },
  sidebarTitle: { fontSize: "18px", fontWeight: 700, marginBottom: "10px" },
  projectList: { display: "flex", flexDirection: "column", gap: "12px", overflowY: "auto", flex: 1, minHeight: 0 },
  projectRow: {
    borderRadius: "16px",
    border: "none",
    padding: "14px",
    background: "#fff",
    boxShadow: "0 6px 18px rgba(59, 130, 246, 0.2)",
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    boxSizing: "border-box",
  },
  projectRowHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px" },
  projectRowTitle: { fontSize: "18px", fontWeight: 700, color: "#0f172a" },
  projectRowSubtitle: { fontSize: "13px", color: "#475569" },
  projectRowRight: { fontSize: "14px", fontWeight: 600, color: "#2563eb" },
  projectBadge: {
    borderRadius: "999px",
    padding: "4px 12px",
    fontSize: "12px",
    fontWeight: 600,
    border: "1px solid #cbd5f5",
  },
  projectBadgePrimary: {
    background: "#dcfce7",
    color: "#047857",
    borderColor: "#34d399",
  },
  projectBadgeSecondary: {
    background: "#e0e7ff",
    color: "#312e81",
    borderColor: "#c7d2fe",
  },
  projectDescriptorBadge: {
    background: "#f0f9ff",
    color: "#1d4ed8",
    borderColor: "#bfdbfe",
  },
  projectRowBadges: { display: "flex", gap: "6px", alignItems: "center" },
  projectCard: {
    borderRadius: "16px",
    border: "1px solid #e2e8f0",
    padding: "14px",
    background: "#fff",
    cursor: "pointer",
    transition: "border-color 0.2s, box-shadow 0.2s",
  },
  projectCardHeader: { display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: "8px" },
  projectCardNumber: { fontSize: "22px", fontWeight: 700, color: "#0f172a" },
  projectCardProduct: { fontSize: "14px", color: "#475569" },
  projectCardBody: { marginTop: "12px", display: "flex", justifyContent: "space-between", alignItems: "center" },
  projectStateBadge: {
    borderRadius: "999px",
    padding: "4px 14px",
    fontSize: "12px",
    fontWeight: 700,
    border: "1px solid #2563eb",
    color: "#2563eb",
  },
  projectActionButton: {
    borderRadius: "999px",
    padding: "4px 16px",
    fontSize: "12px",
    fontWeight: 600,
    border: "none",
    background: "#eef2ff",
    color: "#2563eb",
    cursor: "pointer",
  },
  projectFooter: { marginTop: "8px", fontSize: "12px", color: "#6b7280" },
  main: { flex: "1", minWidth: "320px", display: "flex", flexDirection: "column", gap: "16px", height: "100%", minHeight: 0 },
  sectionCard: { background: "#fff", borderRadius: "16px", padding: "18px", boxShadow: "0 4px 16px rgba(0,0,0,0.08)", flex: "1", minHeight: 0, display: "flex", flexDirection: "column" },
  sectionTitle: { fontSize: "18px", fontWeight: 700, marginBottom: "12px" },
  machineCard: {
    borderRadius: "16px",
    border: "1px solid #e2e8f0",
    background: "#fff",
    boxShadow: "0 4px 16px rgba(15, 23, 42, 0.08)",
    padding: "18px",
    display: "flex",
    flexDirection: "column",
    gap: "14px",
    alignItems: "stretch",
    overflow: "hidden",
  },
  machineList: { display: "flex", flexDirection: "column", gap: "5px" },
  machineCardHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px" },
  machineTitle: { fontSize: "20px", fontWeight: 700, color: "#0f172a" },
  machineTitleRow: { display: "flex", alignItems: "center", gap: "12px" },
  machineInfoRow: { display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap", flex: 1 },
  projectIdBadge: {
    borderRadius: "999px",
    border: "1px solid #e2e8f0",
    background: "#fff",
    padding: "4px 10px",
    fontSize: "12px",
    fontWeight: 600,
    color: "#0f172a",
  },
  ncBadge: {
    borderRadius: "999px",
    border: "1px solid #0f172a",
    padding: "4px 14px",
    fontSize: "12px",
    fontWeight: 600,
    color: "#0f172a",
  },
  machineMeta: { fontSize: "13px", color: "#475569", marginTop: "4px" },
  machineBaselineText: {
    fontSize: "13px",
    color: "#1f2937",
    background: "#f8fafc",
    padding: "8px 12px",
    borderRadius: "10px",
    border: "1px solid #e2e8f0",
    lineHeight: 1.4,
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
  },
  machineBaselineSegment: {
    fontSize: "13px",
    color: "#1f2937",
    background: "#f8fafc",
    padding: "6px 10px",
    borderRadius: "8px",
    border: "1px solid #e2e8f0",
    whiteSpace: "nowrap",
  },
  machineSupport: { fontSize: "13px", color: "#334155", marginTop: "6px", background: "#f1f5f9", padding: "6px 12px", borderRadius: "10px", border: "1px solid #e2e8f0" },
  machineHeaderRow: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: "16px" },
  machineStatusRow: { display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" },
  machineOperationLabel: {
    fontSize: "12px",
    fontWeight: 600,
    color: "#047857",
    borderRadius: "999px",
    border: "1px solid #86efac",
    background: "#dcfce7",
    padding: "4px 10px",
  },
  aiScoreBadge: {
    borderRadius: "999px",
    border: "1px solid #cbd5f5",
    background: "#f8fafc",
    color: "#1d4ed8",
    padding: "4px 10px",
    fontSize: "12px",
    fontWeight: 600,
  },
  analysisHighlightContainer: {
    borderRadius: "12px",
    border: "1px solid #e2e8f0",
    background: "#f8fafc",
    padding: "12px",
    marginTop: "12px",
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  analysisHighlightTitle: {
    fontSize: "13px",
    fontWeight: 600,
    color: "#0f172a",
  },
  analysisHighlightLine: {
    fontSize: "13px",
    color: "#475569",
    lineHeight: 1.4,
  },
  aiMetricLabel: {
    fontSize: "13px",
    color: "#475569",
    fontWeight: 600,
  },
  detailAction: { display: "flex", alignItems: "center" },
  aiInfoText: { fontSize: "13px", color: "#475569" },
  detailSummary: { display: "flex", gap: "12px", alignItems: "center", flexWrap: "wrap" },
  machineInfoBadges: { display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center" },
  baselineRow: { display: "flex", gap: "6px", flexWrap: "wrap", alignItems: "center", marginTop: "6px" },
  baselineBadge: {
    borderRadius: "999px",
    padding: "4px 12px",
    fontSize: "12px",
    border: "1px solid #d1d5db",
    background: "#f9fafb",
    color: "#1f2937",
  },
  severityLegendGroup: { display: "flex", gap: "6px", alignItems: "center" },
  severityLegendItem: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    fontSize: "12px",
    color: "#475569",
    padding: "4px 10px",
    borderRadius: "999px",
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
  },
  severityLegendDot: {
    width: "8px",
    height: "8px",
    borderRadius: "50%",
    display: "inline-block",
  },
  badgeRow: { display: "flex", gap: "8px", flexWrap: "wrap" },
  badge: {
    padding: "4px 12px",
    borderRadius: "999px",
    fontWeight: 700,
    fontSize: "12px",
    border: "1px solid",
  },
  toggleButton: {
    background: "transparent",
    border: "1px solid #2563eb",
    borderRadius: "999px",
    color: "#2563eb",
    fontWeight: 600,
    padding: "6px 16px",
    cursor: "pointer",
  },
  summaryText: { fontSize: "13px", color: "#475569" },
  detailPanel: {
    borderRadius: "12px",
    border: "1px solid #e2e8f0",
    background: "#f8fafc",
    padding: "14px",
    display: "flex",
    flexDirection: "column",
    gap: "10px",
  },
  detailLine: {
    fontSize: "14px",
    color: "#334155",
    lineHeight: 1.5,
  },
  analysisMeta: { display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "8px" },
  analysisStatus: {
    fontWeight: 700,
    fontSize: "13px",
    padding: "4px 12px",
    borderRadius: "999px",
    border: "1px solid #3b82f6",
    color: "#1d4ed8",
  },
};

const AI_ROUTE_LABELS = {
  LEARN: "기준 대기",
  STATS: "통계 분석",
  AE: "딥러닝 분석",
};
const AI_STATE_LABELS = {
  LEARN: "기준 대기",
  READY: "운영중",
};
const AI_LEVEL_LABELS = {
  OK: "정상",
  WARN: "주의",
  ALERT: "경고",
};

const mapAiRouteLabel = (route) => {
  const key = String(route ?? "").toUpperCase();
  if (key === "COLD") return "검증중";
  return AI_ROUTE_LABELS[key] ?? "미분류";
};
const mapAiStateLabel = (state) => {
  const key = String(state ?? "").toUpperCase();
  return AI_STATE_LABELS[key] ?? "상태 미확인";
};
const mapAiLevelLabel = (level) => {
  if (!level) return "-";
  const key = String(level).toUpperCase();
  return AI_LEVEL_LABELS[key] ?? "미분류";
};
const mapAiWarmupLabel = (warmup) => {
  const key = String(warmup ?? "").toUpperCase();
  return key === "Y" ? "설비예열 중" : null;
};

const extractLastValue = (value) => {
  if (value === null || value === undefined) return null;
  const parts = String(value).split("|").filter((part) => part !== "");
  return parts.length ? parts.at(-1) : String(value);
};

const normalizeReasonText = (reason) => {
  if (!reason) return "사유 없음";
  return String(reason)
    .replace(/공유모델\(company\+recipe\)/g, "공유모델(전체 설비 대상)")
    .replace(/로컬모델\(company\+recipe\)/g, "로컬모델(현재 설비 대상)")
    .replace(/글로벌샘플/g, "전체 설비 데이터")
    .replace(/현재설비샘플/g, "현재 설비 데이터")
    .replace(/글로벌/g, "전체 설비")
    .replace(/로컬/g, "현재 설비");
};

const INITIAL_SUMMARY = {
  ok: 0,
  warn: 0,
  alert: 0,
  topMetrics: [],
  maxScore: 0,
  fallbackCount: 0,
};

const MachineFncAIDetection = () => {
  const navigate = useNavigate();
  const appStore = useContext(AppContext);
  const [MenuID, setMenuID] = useState(0);
  const [MenuName, setMenuName] = useState("");
  const [projects, setProjects] = useState([]);
  const [baselineMap, setBaselineMap] = useState({});
  const [machines, setMachines] = useState([]);
  const [selectedMachineId, setSelectedMachineId] = useState(null);
  const [recentLogMap, setRecentLogMap] = useState({});
  const [summaryStats, setSummaryStats] = useState(INITIAL_SUMMARY);
  const [searchQuery, setSearchQuery] = useState("");
  const recentLimit = 10;

  const withGlobalLoading = async (work) => {
    if (appStore?.loading) return;
    appStore?.setLoading?.(true);
    try {
      await work();
    } catch (error) {
      console.error(error);
    } finally {
      appStore?.setLoading?.(false);
    }
  };

  const loadProjectList = async () => {
    let data = [];
    try {
      const response = await axios.get("/api/p/MachineFncAIDetection/projects");
      data = response?.data ?? [];
    } catch (error) {
      console.error(error);
      data = [];
    }
    if (!Array.isArray(data)) data = [];
    setProjects(data);
    return data;
  };

  const loadProjectBaseline = async (projectName) => {
    if (!projectName) {
      return [];
    }
    if (baselineMap[projectName]) {
      return baselineMap[projectName];
    }
    let data = [];
    try {
      const response = await axios.get(`/api/p/MachineFncAIDetection/projects/${encodeURIComponent(projectName)}/baseline`);
      data = response?.data ?? [];
    } catch (error) {
      console.error(error);
      data = [];
    }
    if (!Array.isArray(data)) data = [];
    setBaselineMap((prev) => ({ ...prev, [projectName]: data }));
    return data;
  };

  const loadMachineRecent = async (machineId, limit = recentLimit) => {
    if (!machineId) {
      setRecentLogMap({});
      setSummaryStats(INITIAL_SUMMARY);
      return [];
    }
    let data = [];
    try {
      const response = await axios.get(`/api/p/MachineFncAIDetection/machines/${machineId}/recent`, {
        params: { n: limit },
      });
      data = response?.data ?? [];
    } catch (error) {
      console.error(error);
      data = [];
    }
    if (!Array.isArray(data)) data = [];
    setRecentLogMap((prev) => {
      const next = { ...prev, [machineId]: data };
      const combinedLogs = Object.values(next).flat();
      setSummaryStats(buildSummaryStats(combinedLogs));
      return next;
    });
    return data;
  };

  const loadMachineSummaryAndRecent = async (machineId = null, limit = recentLimit) => {
    let summary = [];
    try {
      const response = await axios.get("/api/p/MachineFncAIDetection/machines/summary");
      summary = response?.data ?? [];
    } catch (error) {
      console.error(error);
      summary = [];
    }
    if (!Array.isArray(summary)) summary = [];
    setMachines(summary);
    const projectNames = [...new Set(summary.map((machine) => machine.project_name).filter(Boolean))];
    await Promise.all(projectNames.map((name) => loadProjectBaseline(name)));
    // ✅ 최근 이력은 '선택된 1대'만 가져오면 나머지 카드가 전부 '기록 없음'이 됩니다.
    //    요약 화면에서도 각 설비별 최근 이력이 보여야 하므로, 현재 로딩된 설비 목록 전체에 대해 recent를 로딩합니다.
    const currentId = machineId ?? selectedMachineId ?? summary[0]?.machine_id;
    if (summary && summary.length > 0) {
      setSelectedMachineId(currentId ?? summary[0]?.machine_id ?? null);
      // 설비별 최근 로그 로딩 (회사+설비 기준 endpoint)
      await Promise.all(summary.map((m) => loadMachineRecent(m.machine_id, limit)));
    } else {
      setSelectedMachineId(null);
      setRecentLogMap({});
      setSummaryStats(INITIAL_SUMMARY);
    }
    return summary;
  };

  const buildSummaryStats = (logs) => {
    const stats = { ...INITIAL_SUMMARY };
    const metricCounts = {};
    logs.forEach((log) => {
      const level = String(log.ai_level ?? "OK").toUpperCase();
      if (level === "ALERT") stats.alert += 1;
      else if (level === "WARN") stats.warn += 1;
      else stats.ok += 1;
      if (log.ai_metric_name) {
        metricCounts[log.ai_metric_name] = (metricCounts[log.ai_metric_name] || 0) + 1;
      }
      if (String(log.ai_reason ?? "").toUpperCase().includes("FALLBACK")) {
        stats.fallbackCount += 1;
      }
      const score = Number(log.ai_score);
      if (!Number.isNaN(score) && score > stats.maxScore) {
        stats.maxScore = score;
      }
    });
    stats.topMetrics = Object.entries(metricCounts)
      .sort(([, aCount], [, bCount]) => bCount - aCount)
      .slice(0, 3)
      .map(([name, count]) => `${name} (${count})`);
    return stats;
  };

  const handleView = (silent = false) => {
    const run = async () => {
      const projectList = await loadProjectList();
      await loadMachineSummaryAndRecent();
    };

    // 자동조회(silent)는 로딩바를 띄우지 않음
    if (silent) {
      run();
      return;
    }

    withGlobalLoading(async () => {
      await run();
    });
  };

  const handleMachineSelect = (machineId) => {
    if (!machineId || machineId === selectedMachineId) return;
    withGlobalLoading(async () => {
      await loadMachineSummaryAndRecent(machineId);
    });
  };


  useEffect(() => {
    const accessList = JSON.parse(localStorage.getItem("nav"));
    if (Array.isArray(accessList)) {
      const currentPath = String(window.location.pathname)
        .split("/")
        .slice(0, 3)
        .join("/");
      const matched = accessList.find((item) => String(item.menu_url) === currentPath);
      if (matched) {
        setMenuID(matched.menu_id);
        setMenuName(matched.menu_name);
      } else {
        Swal.fire({
          title: "<p style=\"color:red\">접근 권한 오류</p>",
          text: "화면에 접근 권한이 없습니다.",
          confirmButtonText: "확인",
          confirmButtonColor: "#7066e0",
          timer: 5000,
          timerProgressBar: true,
        }).then(() => {
          navigate(-1);
        });
      }
    }
    const timer = setInterval(() => {
      handleView(true);
    }, 60000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (String(MenuID) !== String(0) && MenuID != null) {
      handleView();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [MenuID]);

  const filteredProjects = useMemo(() => {
    if (!searchQuery) return projects;
    return projects.filter((project) =>
      (project.project_name ?? "").toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [projects, searchQuery]);

  const translateProjectState = (state) => {
    const upper = String(state ?? "LEARN").toUpperCase();
    if (upper === "LEARN") return "학습중";
    if (upper === "READY") return "운영중";
    if (upper === "DISABLED") return "비활성";
    return upper;
  };

  const translateAiLevelLabel = (level) => {
    if (!level) return "-";
    const upper = level.toUpperCase();
    if (upper === "OK") return "정상";
    if (upper === "WARN") return "주의";
    if (upper === "ALERT") return "경고";
    return upper;
  };

  const hasBaselineMissingReason = (reason) => {
    const text = String(reason ?? "").toUpperCase();
    return text.includes("기준선 없음") || text.includes("NO BASELINE");
  };

  const getAiLevelDisplay = (level, reason) => {
    const baselineMissing = hasBaselineMissingReason(reason);
    if (baselineMissing) {
      return {
        label: "분석 준비",
        borderColor: "#fcd34d",
        textColor: "#92400e",
        background: "#fef3c7",
      };
    }
    const label = translateAiLevelLabel(level);
    if (label === "-") {
      return { label, borderColor: "#e5e7eb", textColor: "#6b7280", background: "#f9fafb" };
    }
    if (label === "정상") {
      return { label, borderColor: "#bbf7d0", textColor: "#166534", background: "#ecfccb" };
    }
    if (label === "주의") {
      return { label, borderColor: "#fdba74", textColor: "#c2410c", background: "#ffedd5" };
    }
    if (label === "경고") {
      return { label, borderColor: "#fecaca", textColor: "#b91c1c", background: "#fee2e2" };
    }
    return { label, borderColor: "#cbd5f5", textColor: "#0f172a", background: "#e0e7ff" };
  };

  const getRunStatus = (status) => {
    const map = {
      "0": { label: "오프라인", className: "badge offline" },
      "1": { label: "정지", className: "badge stop" },
      "2": { label: "홀드", className: "badge hold" },
      "3": { label: "가동", className: "badge run" },
      "4": { label: "MSTR", className: "badge mstr" },
      "-1": { label: "미 연결", className: "badge notconnect" },
    };
    return map[String(status)] || { label: status || "알 수 없음", className: "badge" };
  };

  const getAiLevelClass = (level) => {
    if (!level) return "badge";
    const name = level?.toUpperCase();
    if (name === "OK") return "badge run";
    if (name === "WARN") return "badge stop";
    if (name === "ALERT") return "badge hold";
    return "badge";
  };

  const formatBaselineValue = (value) => {
    if (value === null || typeof value === "undefined" || value === "") return "-";
    const number = Number(value);
    return Number.isNaN(number) ? String(value) : number.toFixed(2);
  };

  function formatTimeHHmmss(input) {
    if (input === null || input === undefined) return "";

    // 이미 "HH:mm:ss"
    if (typeof input === "string" && /^\d{2}:\d{2}:\d{2}$/.test(input)) return input;

    // "HHmmss"
    if (typeof input === "string" && /^\d{6}$/.test(input)) {
      return `${input.slice(0, 2)}:${input.slice(2, 4)}:${input.slice(4, 6)}`;
    }

    let d = null;

    // Date
    if (input instanceof Date) {
      d = input;
    } else if (typeof input === "number") {
      // timestamp (ms) or (sec)
      d = new Date(input < 1e12 ? input * 1000 : input);
    } else if (typeof input === "string") {
      // "YYYY-MM-DD HH:mm:ss" 같이 공백 형식도 Date가 먹을 수 있게 처리
      const safe = input.includes(" ") && !input.includes("T") ? input.replace(" ", "T") : input;
      const tmp = new Date(safe);
      if (!isNaN(tmp.getTime())) d = tmp;
    }

    if (!d || isNaN(d.getTime())) return "";

    const hh = String(d.getHours()).padStart(2, "0");
    const mm = String(d.getMinutes()).padStart(2, "0");
    const ss = String(d.getSeconds()).padStart(2, "0");
    return `${hh}:${mm}:${ss}`;
  }
  
  const formatNumber = (value) => {
    if (value === null || typeof value === "undefined" || value === "") return "-";
    const number = Number(value);
    return Number.isNaN(number) ? String(value) : number.toLocaleString();
  };

  const formatProductName = (name) => {
    if (!name) return "제품 정보 없음";
    const trimmed = typeof name === "string" ? name.trim() : String(name);
    if (trimmed.length <= 12) return trimmed;
    return `${trimmed.slice(0, 12)}...`;
  };

  const formatDateTime = (value) => {
    if (!value) return "-";
    return Moment(value).format("YYYY.MM.DD HH:mm");
  };

  const formatRecentLogLine = (log, options = {}) => {
    const timestamp = log.reg_datetime
      ? Moment(log.reg_datetime).format("HH:mm:ss")
      : "-";
    const routeLabel = mapAiRouteLabel(log.ai_route);
    const stateLabel = mapAiStateLabel(log.ai_state);
    const scoreText =
      log.ai_score_100 !== null &&
      typeof log.ai_score_100 !== "undefined" &&
      log.ai_score_100 !== ""
        ? String(Math.round(Number(log.ai_score_100)))
        : log.ai_score === null || typeof log.ai_score === "undefined"
        ? null
        : Number(log.ai_score).toFixed(2);
    const reasonLabel = normalizeReasonText(log.ai_reason);
    const warmupLabel = mapAiWarmupLabel(log.ai_warmup_yn);
    const globalRows = log.ai_global_rows != null ? `전체 설비 데이터 ${formatNumber(log.ai_global_rows)}` : null;
    const localRows = log.ai_local_rows != null ? `현재 설비 데이터 ${formatNumber(log.ai_local_rows)}` : null;
    const spindleLoad = extractLastValue(log.spindle_load);
    const spindleValue = extractLastValue(log.spindle);
    const axisXLoad = extractLastValue(log.axis_x_load);
    const axisYLoad = extractLastValue(log.axis_y_load);
    const axisZLoad = extractLastValue(log.axis_z_load);
    const values = [
      spindleLoad && `스핀들로드 ${spindleLoad}`,
      axisXLoad && `X축 로드 ${axisXLoad}`,
      axisYLoad && `Y축 로드 ${axisYLoad}`,
      axisZLoad && `Z축 로드 ${axisZLoad}`,
    ].filter(Boolean);
    const mlValues = values.join(" · ");
    const produceDiff = options.produceDiff;
    const produceCount = typeof produceDiff === "number" ? produceDiff : null;
    const produceText =
      produceCount !== null
        ? `생산수량 ${formatNumber(produceCount)}`
        : null;
    return {
      timeLabel: timestamp,
      routeLabel,
      stateLabel,
      scoreText,
      reasonLabel,
      warmupLabel,
      globalRows,
      localRows,
      spindleLoad,
      spindleValue,
      axisXLoad,
      axisYLoad,
      axisZLoad,
      mlValues,
      produceText,
      produceCount,
    };
  };

const computeProduceDeltaMap = (logs) => {
  const map = {};
  for (let idx = 0; idx < logs.length; idx += 1) {
    const current = logs[idx];
    const next = logs[idx + 1];
    const currentValue =
      current?.produce_count === null || typeof current?.produce_count === "undefined"
        ? null
        : Number(current.produce_count);
    const nextValue =
      next?.produce_count === null || typeof next?.produce_count === "undefined"
        ? null
        : Number(next.produce_count);
    if (currentValue === null || Number.isNaN(currentValue)) {
      map[current.reg_datetime] = 0;
      continue;
    }
    if (nextValue === null || Number.isNaN(nextValue)) {
      map[current.reg_datetime] = currentValue;
      continue;
    }
    map[current.reg_datetime] = Math.max(currentValue - nextValue, 0);
  }
  return map;
};

  const describeNaturalLogSentence = (ctx) => {
    const machineName = toTextLabel(ctx?.machineName ?? "-");
    const projectName = toTextLabel(ctx?.projectName ?? "-");

    let aiState = String(ctx?.aiState ?? "").toUpperCase();
    const aiLevel = String(ctx?.aiLevel ?? "").toUpperCase();
    const reasonRaw = String(ctx?.log?.ai_reason ?? ctx?.reason ?? "");

    // ✅ ai_state가 비어있을 때 보정
    if (!aiState) {
      const up = reasonRaw.toUpperCase();
      if (up.includes("기준선 없음") || up.includes("학습 필요") || up.includes("데이터") && up.includes("부족")) {
        aiState = "LEARN";
      } else if (aiLevel === "OK" || aiLevel === "WARN" || aiLevel === "ALERT") {
        aiState = "READY";
      }
    }

    const score100 = (ctx?.score100 !== null && ctx?.score100 !== undefined) ? Number(ctx.score100) : null;
    const cur = (ctx?.cur !== null && ctx?.cur !== undefined) ? Number(ctx.cur) : null;
    const need = (ctx?.need !== null && ctx?.need !== undefined) ? Number(ctx.need) : null;
    const lack = (ctx?.lack !== null && ctx?.lack !== undefined) ? Number(ctx.lack) : null;

    if (aiState === "LEARN") {
      const ratio = (Number.isFinite(cur) && Number.isFinite(need)) ? ` (데이터 ${cur}/${need}${Number.isFinite(lack) ? `, 부족 ${lack}` : ""})` : "";
      return `${machineName} 프로젝트 ${projectName}은 기준 데이터 준비중입니다.${ratio}`;
    }

    if (aiState === "READY") {
      const levelText = aiLevel === "WARN" ? "주의" : (aiLevel === "ALERT" ? "경고" : "정상");
      if (Number.isFinite(score100)) return `${machineName} 프로젝트 ${projectName}은 분석 결과 ${levelText}입니다. (AI점수 ${Math.round(score100)})`;
      return `${machineName} 프로젝트 ${projectName}은 분석 결과 ${levelText}입니다.`;
    }

    return `${machineName} 프로젝트 ${projectName}은 상태를 확인 중입니다.`;
  };

  const getModelDescriptor = (project) => {
    if (!project) return "학습전";
    const state = String(project.state ?? "").toUpperCase();
    if (state === "DISABLED") return "학습전";
    const sampleCount = Number(project.sample_count ?? 0);
    if (Number.isNaN(sampleCount) || sampleCount < 60) {
      return "학습전";
    }
    const type = String(project.model_type ?? "").toLowerCase();
    if (type.includes("autoencoder") || type.includes("ae") || type.includes("deep")) return "딥러닝학습";
    if (type.includes("stats") || type.includes("ml") || type === "") return "머신러닝학습";
    return "초기학습";
  };

  
  // 이력(상세) 1줄: "시간 + 자연어 + (항목형 상세)"
  const toTextLabel = (v) => {
    if (v === null || v === undefined) return "";
    if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") return String(v);
    if (typeof v === "object") {
      return String(v.label ?? v.name ?? v.value ?? v.text ?? v.title ?? "");
    }
    return String(v);
  };

  // 값이 "1.0|2.0|3.0" 형태면 마지막 값을 사용(가장 최신값)
  const parseScalar = (v, fallback = 0) => {
    if (v === null || v === undefined) return fallback;
    if (typeof v === "number") return Number.isFinite(v) ? v : fallback;
    const s = String(v).trim();
    if (!s) return fallback;
    const last = s.includes("|") ? s.split("|").filter(Boolean).slice(-1)[0] : s;
    const n = Number(last);
    return Number.isFinite(n) ? n : fallback;
  };

  // key_metrics 배열(서버가 내려주는 대표 지표)이 있으면 우선 사용
  const pickKeyMetric = (log, metricName) => {
    const arr = Array.isArray(log?.key_metrics) ? log.key_metrics : [];
    const hit = arr.find((x) => String(x?.metric_name || "") === String(metricName));
    return hit?.current_value ?? null;
  };

  const buildHistoryOneLine = (log, machineInfo) => {
    const dt = log?.reg_datetime ?? log?.ai_infer_datetime ?? log?.regDatetime ?? log?.infer_datetime;
    const time = formatTimeHHmmss(dt);

    const machineName = toTextLabel(log?.machine_name ?? machineInfo?.machine_name ?? machineInfo?.machineName ?? "CNC");
    const projectName = toTextLabel(log?.project_name ?? machineInfo?.project_name ?? machineInfo?.projectName ?? "-");

    const aiState = String(log?.ai_state ?? "").toUpperCase(); // READY / LEARN ...
    const aiLevel = String(log?.ai_level ?? "").toUpperCase(); // OK / WARN / ALERT ...
    const score100 = (log?.ai_score_100 !== null && log?.ai_score_100 !== undefined) ? Number(log.ai_score_100) : null;

    // 표시용 현재값(파이프 문자열이면 마지막 값)
    const spindleLoad = parseScalar(pickKeyMetric(log, "spindle_load_avg") ?? log?.spindle_load, 0);
    const xLoad = parseScalar(pickKeyMetric(log, "axis_x_load_avg") ?? log?.axis_x_load, 0);
    const yLoad = parseScalar(pickKeyMetric(log, "axis_y_load_avg") ?? log?.axis_y_load, 0);

    const produceCount = (log?.produce_count !== null && log?.produce_count !== undefined)
      ? Number(log.produce_count)
      : (log?.produceCount !== null && log?.produceCount !== undefined ? Number(log.produceCount) : null);

    // "데이터 부족" 숫자 추출: ai_reason 안에 "156/200" 또는 "부족 44" 등이 들어올 수 있음
    const reason = String(log?.ai_reason ?? "");
    let cur = null, need = null, lack = null;
    const mRatio = reason.match(/(\d+)\s*\/\s*(\d+)/);
    if (mRatio) { cur = Number(mRatio[1]); need = Number(mRatio[2]); }
    const mLack = reason.match(/부족\s*(\d+)/);
    if (mLack) { lack = Number(mLack[1]); }

    // 자연어 1줄(상단 AI 이상감지 2줄용)
    const sentence = describeNaturalLogSentence({
      machineName,
      projectName,
      aiState,
      aiLevel,
      score100,
      cur,
      need,
      lack,
      log,
    });

    // 상세 1줄(하단 이력용): 자연어 + 괄호 상세
    const detailParts = [];
    if (Number.isFinite(spindleLoad)) detailParts.push(`스핀들로드 ${spindleLoad}`);
    if (Number.isFinite(xLoad)) detailParts.push(`X로드 ${xLoad}`);
    if (Number.isFinite(yLoad)) detailParts.push(`Y로드 ${yLoad}`);
    if (Number.isFinite(produceCount)) detailParts.push(`생산수량 ${produceCount}`);

    const detail = detailParts.length ? `${sentence} (${detailParts.join(", ")})` : sentence;

    return { timeLabel: time, sentence, detail };
  };;

  const buildAnalysisLines = (logs, machineInfo) => {
    const arr = Array.isArray(logs) ? logs : [];
    return arr
      .map((log) => {
        const o = buildHistoryOneLine(log, machineInfo);
        return {
          timeLabel: o?.timeLabel ?? "",
          description: (o?.detail ?? o?.sentence ?? "").toString().trim(), // ✅ detail 우선
        };
      })
      .filter((x) => x.description.length > 0);
  };

  const buildAnalysisHighlights = (machine) => {
    const rawLogs = (recentLogMap[machine.machine_id] ?? []).slice(0, 10);
    if (rawLogs.length === 0) {
      const fallback = machine.ai_reason ? machine.ai_reason : "AI 추론 결과를 기다리는 중입니다.";
      const intro = `${machine.machine_name ?? "설비"} (프로젝트 ${machine.project_name ?? "-"})`;
      return [{ timeLabel: "", description: `${intro} · ${fallback}` }];
    }
    const produceDeltaMap = computeProduceDeltaMap(rawLogs);
    return rawLogs.slice(0, 2).map((log) => {
      const formatted = formatRecentLogLine(log, { produceDiff: produceDeltaMap[log.reg_datetime] });
      const description = buildHistoryOneLine(log, machine).sentence;
      return { timeLabel: formatted.timeLabel, description };
    });
  };

  const isAutoencoderModel = (modelType) => {
    const type = (modelType ?? "").toLowerCase();
    return type.includes("autoencoder") || type.includes("ae") || type.includes("deep");
  };

  const isStatsModel = (modelType) => {
    const type = (modelType ?? "").toLowerCase();
    return type.includes("stats") || type.includes("ml") || type === "";
  };

  const describeBaselineEntries = (label, entries) => {
    if (!entries || entries.length === 0) {
      return `${label} 기준 데이터 준비 중`;
    }
    const summary = entries
      .slice(0, 2)
      .map((entry) => {
        const metric = entry.metric_name ?? "지표";
        const mean = formatBaselineValue(entry.mean_value);
        const max = formatBaselineValue(entry.max_value);
        return `${metric} (mean ${mean}, max ${max})`;
      })
      .join(" / ");
    return `${label} 기준 데이터 · ${summary}`;
  };

  const buildMachineLearningBaselineLine = (machine) => {
    const projectName = machine.project_name ?? "";
    const entries = baselineMap[projectName] ?? [];
    const statsEntries = entries.filter((entry) => isStatsModel(entry.model_type));
    return describeBaselineEntries("머신러닝", statsEntries);
  };

  const buildDeepLearningBaselineLine = (machine) => {
    const projectName = machine.project_name ?? "";
    const entries = baselineMap[projectName] ?? [];
    const deepEntries = entries.filter((entry) => isAutoencoderModel(entry.model_type));
    return describeBaselineEntries("딥러닝", deepEntries);
  };

  const [expandedMachines, setExpandedMachines] = useState(new Set());
  const toggleExpansion = (machineId) => {
    setExpandedMachines((prev) => {
      const next = new Set(prev);
      if (next.has(machineId)) next.delete(machineId);
      else next.add(machineId);
      return next;
    });
  };

  const selectedMachine = useMemo(() => {
    if (!machines.length || selectedMachineId == null) return null;
    return machines.find((m) => Number(m.machine_id) === Number(selectedMachineId)) ?? null;
  }, [machines, selectedMachineId]);

  const selectedAiDisplay = useMemo(() => {
    if (!selectedMachine) return null;
    return getAiLevelDisplay(selectedMachine.ai_level, selectedMachine.ai_reason);
  }, [selectedMachine]);

  const selectedAiScore = useMemo(() => {
    if (!selectedMachine) return "-";
    return hasBaselineMissingReason(selectedMachine.ai_reason)
      ? "-"
      : formatBaselineValue(selectedMachine.ai_score);
  }, [selectedMachine]);

  return (
    <div style={{ width: "100%", display: String(MenuID) === String(0) ? "hidden" : "block" }}>
      <div className="menubox" style={{ height: "70px", boxShadow: "none" }}>
        <div className="menu-action" style={{ height: "55px" }}>
          <div className="menu-title">{MenuName}</div>
        </div>
      </div>
      <div className="grid" style={{ top: "73px", height: "calc(100% - 73px)" }}>
        <div className="page" style={layoutStyles.page}>
          <div style={layoutStyles.container}>
            <div style={layoutStyles.sidebar}>
              <div style={layoutStyles.sidebarTitle}>NC 프로젝트</div>
              <input
                type="text"
                className="input"
                placeholder="프로젝트명 검색"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{ width: "100%", marginBottom: "10px" }}
              />
              <div style={layoutStyles.projectList}>
                {filteredProjects.map((project) => {
                  const descriptor = getModelDescriptor(project);
                  return (
                    <div key={project.project_name} style={layoutStyles.projectRow}>
                      <div style={layoutStyles.projectRowHeader}>
                        <div>
                          <div style={layoutStyles.projectRowTitle}>{project.project_name}

                            <span style={{
                                ...layoutStyles.projectRowSubtitle,
                                fontSize: "12px",
                                fontWeight: 500,
                              }}
                            >&nbsp;
                              {project.product_name
                                ? `(${formatProductName(project.product_name)})`
                                : "제품 정보 없음"}
                            </span>
                          </div>
                          
                        </div>
                        <div style={layoutStyles.projectRowRight}>{formatNumber(project.sample_count)}</div>
                      </div>
                      <div style={layoutStyles.projectRowBadges}>
                        <span style={{ ...layoutStyles.projectBadge, ...layoutStyles.projectBadgePrimary }}>
                          {translateProjectState(project.state)}
                        </span>
                        <span
                          style={{
                            ...layoutStyles.projectBadge,
                            ...layoutStyles.projectDescriptorBadge,
                          }}
                        >
                          {descriptor}
                        </span>
                      </div>
                    </div>
                  );
                })}
                {filteredProjects.length === 0 && (
                  <p style={{ color: "#6b7280", fontSize: "13px" }}>검색 결과가 없습니다.</p>
                )}
              </div>
            </div>
            <div style={layoutStyles.main}>
              <div style={layoutStyles.sectionCard}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px", marginBottom: "12px" }}>
                  <div style={layoutStyles.sectionTitle}>AI 이상감지</div>
                  <div style={layoutStyles.severityLegendGroup}>
                    {[
                      { label: "정상", text: "AI 점수 < 30", color: "#10b981" },
                      { label: "주의", text: "30 ≤ AI 점수 < 60", color: "#f97316" },
                      { label: "경고", text: "AI 점수 ≥ 60", color: "#ef4444" },
                    ].map((item) => (
                      <span key={item.label} style={layoutStyles.severityLegendItem}>
                        <span style={{ ...layoutStyles.severityLegendDot, backgroundColor: item.color }} />
                        <span>{item.label}</span>
                        <span>{item.text}</span>
                      </span>
                    ))}
                  </div>
                </div>
                <div style={layoutStyles.machineList}>
                  {machines.map((machine) => {
                    const runMeta = getRunStatus(machine.status_run ?? machine.run_status);
                    const projectName = machine.project_name ?? "-";
                    const aiDisplay = getAiLevelDisplay(machine.ai_level, machine.ai_reason);
                    const rawLogs = (recentLogMap[machine.machine_id] ?? []).slice(0, 10);
                    const analysisLines = buildAnalysisLines(rawLogs, machine);
                    const analysisHighlights = buildAnalysisHighlights(machine);
                    const machineLearningBaseline = buildMachineLearningBaselineLine(machine);
                    const deepLearningBaseline = buildDeepLearningBaselineLine(machine);
                    const isExpanded = expandedMachines.has(machine.machine_id);
                    const aiScoreText = hasBaselineMissingReason(machine.ai_reason)
                      ? "-"
                      : (machine.ai_score_100 !== null && typeof machine.ai_score_100 !== "undefined" && machine.ai_score_100 !== "")
                        ? String(Math.round(Number(machine.ai_score_100)))
                        : formatBaselineValue(machine.ai_score);
                    const routeLabel = mapAiRouteLabel(machine.ai_route);
                    const stateLabel = mapAiStateLabel(machine.ai_state);
                    const warmupLabel = mapAiWarmupLabel(machine.ai_warmup_yn);
                    return (
                      <div key={machine.machine_id} style={layoutStyles.machineCard}>
                        <div style={layoutStyles.machineHeaderRow}>
                          <div style={layoutStyles.machineInfoRow}>
                            <div style={layoutStyles.machineTitleRow}>
                              <div style={layoutStyles.machineTitle}>{machine.machine_name}</div>
                              <span style={layoutStyles.ncBadge}>{projectName}</span>
                            </div>
                            <div style={layoutStyles.machineInfoBadges}>
                              <span
                                className={runMeta.className}
                                style={{
                                  ...layoutStyles.badge,
                                  borderColor: runMeta.className.includes("stop") ? "#b91c1c" : "#0f172a",
                                  color: runMeta.className.includes("stop") ? "#b91c1c" : "#0f172a",
                                  background: runMeta.className.includes("stop") ? "#fef2f2" : "#fff",
                                }}
                              >
                                {runMeta.label}
                              </span>
                              {null}
                              <span
                                style={{
                                  ...layoutStyles.badge,
                                  borderColor: "#0b8566",
                                  color: "#0b8566",
                                  background: "#ecfdf3",
                                }}
                              >
                                {stateLabel}
                              </span>
                              <span
                                style={{
                                  ...layoutStyles.badge,
                                  borderColor: aiDisplay.borderColor,
                                  color: aiDisplay.textColor,
                                  background: aiDisplay.background,
                                }}
                              >
                                {aiDisplay.label}
                              </span>
                              <span style={layoutStyles.aiScoreBadge}>AI 점수 {aiScoreText}</span>
                              <span style={layoutStyles.baselineBadge}>{machineLearningBaseline}</span>
                              <span style={layoutStyles.baselineBadge}>{deepLearningBaseline}</span>
                            </div>
                          </div>
                          <div style={layoutStyles.detailAction}>
                            <button type="button" style={layoutStyles.toggleButton} onClick={() => toggleExpansion(machine.machine_id)}>
                              {isExpanded ? "요약" : "상세"}
                            </button>
                          </div>
                        </div>
                        <div style={layoutStyles.analysisHighlightContainer}>
                          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                            {analysisHighlights.map((line, idx) => (
                              <div key={`highlight-${machine.machine_id}-${idx}`} style={layoutStyles.analysisHighlightLine}>
                                {line.timeLabel && <strong>{line.timeLabel}</strong>}
                                {line.timeLabel ? " · " : ""}
                                {line.description}
                              </div>
                            ))}
                          </div>
                        </div>
                        {isExpanded && (
                          <div style={layoutStyles.detailPanel}>
                            {analysisLines.map((line, idx) => (
                              <div key={`${machine.machine_id}-line-${idx}`} style={layoutStyles.detailLine}>
                                {line.timeLabel ? <strong>{line.timeLabel}</strong> : null}
                                {line.timeLabel ? " · " : ""}
                                {line.description}
                              </div>
                            ))}
                          </div>
                        )}
                    </div>
                    );
                  })}
                </div>
                {machines.length === 0 && <p>설비 정보를 불러오는 중입니다.</p>}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MachineFncAIDetection;